import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getRelatorio } from "../../services/fichaService";

const moeda = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const data = (v) => (v ? new Date(v).toLocaleDateString("pt-BR") : "");
const STATUS = { D: "Deferido", R: "Recusado", P: "Em análise" };

// Linha rótulo: valor
const Campo = ({ rotulo, valor }) => (
  <Box sx={{ display: "flex", gap: 0.5, fontSize: 12, py: 0.2 }}>
    <Typography component="span" sx={{ fontWeight: 700, fontSize: 12 }}>{rotulo}:</Typography>
    <Typography component="span" sx={{ fontSize: 12 }}>{valor || "—"}</Typography>
  </Box>
);

const Secao = ({ titulo, children }) => (
  <Box sx={{ mb: 1.5, breakInside: "avoid" }}>
    <Box sx={{ bgcolor: "#1f3a6e", color: "#fff", px: 1, py: 0.5, fontWeight: 700, fontSize: 12 }}>{titulo}</Box>
    <Box sx={{ border: "1px solid #ccc", borderTop: "none", p: 1 }}>{children}</Box>
  </Box>
);

const Tabela = ({ colunas, linhas, render }) => (
  <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 11, "& th,& td": { border: "1px solid #ccc", px: 0.5, py: 0.3, textAlign: "left" }, "& th": { bgcolor: "#f0f2f5" } }}>
    <thead><tr>{colunas.map((c) => <th key={c}>{c}</th>)}</tr></thead>
    <tbody>
      {linhas.length === 0
        ? <tr><td colSpan={colunas.length} style={{ textAlign: "center", color: "#888" }}>Nenhum registro.</td></tr>
        : linhas.map((l, i) => <tr key={i}>{render(l)}</tr>)}
    </tbody>
  </Box>
);

export default function RelatorioFicha() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [erro, setErro] = useState("");

  useEffect(() => {
    (async () => {
      try { setD((await getRelatorio(id)).data); }
      catch (e) { setErro(e?.response?.data || e.message); }
    })();
  }, [id]);

  if (erro) return <Box sx={{ p: 4 }}><Typography color="error">Erro: {String(erro)}</Typography></Box>;
  if (!d) return <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>;

  const c = d.Candidato || {};
  const r = d.Responsavel || {};

  return (
    <Box sx={{ bgcolor: "#eef1f6", minHeight: "100vh", py: 2 }}>
      {/* Barra de ações (não imprime) */}
      <Box sx={{ maxWidth: 800, mx: "auto", mb: 1, "@media print": { display: "none" } }}>
        <Stack direction="row" justifyContent="space-between">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>Voltar</Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>Imprimir / Salvar PDF</Button>
        </Stack>
      </Box>

      {/* Folha A4 */}
      <Box sx={{ maxWidth: 800, mx: "auto", bgcolor: "#fff", p: 3, boxShadow: 2, "@media print": { boxShadow: "none", maxWidth: "none", p: 0 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Ficha Socioeconômica</Typography>
          <Typography sx={{ fontWeight: 700 }}>Protocolo Nº {d.Protocolo}</Typography>
        </Stack>
        <Typography variant="body2" sx={{ mb: 2, color: "#555" }}>
          {d.Edicao} · Situação: {d.Situacao || "—"} · Entrega: {d.SituacaoEntrega || "—"} · Cadastro: {data(d.DtCadastro)}
        </Typography>

        <Secao titulo="I. Identificação do(a) candidato(a)">
          <Campo rotulo="Nome" valor={c.Nome} />
          <Stack direction="row" gap={3} flexWrap="wrap">
            <Campo rotulo="CPF" valor={c.Cpf} />
            <Campo rotulo="Telefone" valor={c.Telefone} />
            <Campo rotulo="Sexo" valor={c.Sexo} />
            <Campo rotulo="Nascimento" valor={data(c.DataNascimento)} />
            <Campo rotulo="E-mail" valor={c.Email} />
          </Stack>
          <Stack direction="row" gap={3} flexWrap="wrap">
            <Campo rotulo="Endereço" valor={`${c.Endereco || ""} ${c.Numero || ""} ${c.Complemento || ""}`.trim()} />
            <Campo rotulo="Bairro" valor={c.Bairro} />
            <Campo rotulo="Cidade" valor={c.Cidade} />
            <Campo rotulo="UF" valor={c.Estado} />
            <Campo rotulo="CEP" valor={c.Cep} />
          </Stack>
        </Secao>

        <Secao titulo="II. Responsável Financeiro">
          <Campo rotulo="Vínculo com o(a) candidato(a)" valor={r.Parentesco} />
          <Stack direction="row" gap={3} flexWrap="wrap">
            <Campo rotulo="Nome" valor={r.Nome} />
            <Campo rotulo="CPF" valor={r.Cpf} />
            <Campo rotulo="RG" valor={r.Rg} />
          </Stack>
          <Stack direction="row" gap={3} flexWrap="wrap">
            <Campo rotulo="Tel. residencial" valor={r.Telefone} />
            <Campo rotulo="Celular" valor={r.Celular} />
            <Campo rotulo="E-mail" valor={r.Email} />
          </Stack>
          <Stack direction="row" gap={3} flexWrap="wrap">
            <Campo rotulo="Renda bruta" valor={moeda(r.RendaBruta)} />
            <Campo rotulo="Renda familiar" valor={moeda(r.RendaFamiliar)} />
          </Stack>
        </Secao>

        <Secao titulo="III. Resumo financeiro">
          <Stack direction="row" gap={3} flexWrap="wrap">
            <Campo rotulo="Total receitas" valor={moeda(d.Totais.TotalReceitas)} />
            <Campo rotulo="Total despesas" valor={moeda(d.Totais.TotalDespesas)} />
            <Campo rotulo="Pessoas na família" valor={d.Totais.QtdPessoasFamilia} />
            <Campo rotulo="Renda per capita" valor={moeda(d.Totais.RendaPerCapita)} />
          </Stack>
        </Secao>

        <Box sx={{ display: "flex", gap: 1.5, "@media print": { display: "block" } }}>
          <Box sx={{ flex: 1 }}>
            <Secao titulo="Despesas do último mês">
              <Tabela colunas={["Descrição", "Valor"]} linhas={d.Despesas}
                render={(l) => <><td>{l.Descricao}</td><td>{moeda(l.Valor)}</td></>} />
            </Secao>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Secao titulo="Receitas / renda do último mês">
              <Tabela colunas={["Descrição", "Valor"]} linhas={d.Receitas}
                render={(l) => <><td>{l.Descricao}</td><td>{moeda(l.Valor)}</td></>} />
            </Secao>
          </Box>
        </Box>

        <Secao titulo="IV. Composição Familiar">
          <Tabela colunas={["Nome", "Parentesco", "Idade", "Est. civil", "Escolaridade", "Profissão", "Salário"]} linhas={d.Familia}
            render={(m) => <><td>{m.Nome}</td><td>{m.Parentesco}</td><td>{m.Idade}</td><td>{m.EstadoCivil}</td><td>{m.Escolaridade}</td><td>{m.Profissao}</td><td>{moeda(m.SalarioBruto)}</td></>} />
        </Secao>

        <Secao titulo="V. Patrimônio">
          <Typography sx={{ fontWeight: 700, fontSize: 11, mb: 0.3 }}>Bens imóveis e móveis</Typography>
          <Tabela colunas={["Tipo", "Descrição", "Localização", "Valor"]} linhas={d.Imoveis}
            render={(p) => <><td>{p.Tipo}</td><td>{p.Descricao}</td><td>{p.Localizacao}</td><td>{moeda(p.ValorMercado)}</td></>} />
          <Typography sx={{ fontWeight: 700, fontSize: 11, mt: 1, mb: 0.3 }}>Veículos</Typography>
          <Tabela colunas={["Modelo", "Ano", "Placa", "Valor"]} linhas={d.Veiculos}
            render={(p) => <><td>{p.Descricao}</td><td>{p.Ano}</td><td>{p.Placa}</td><td>{moeda(p.ValorMercado)}</td></>} />
        </Secao>

        <Secao titulo="VI. Situação de Saúde do Grupo Familiar">
          <Tabela colunas={["Nome", "Parentesco", "Doença crônica", "Deficiência", "Em tratamento"]} linhas={d.Saude}
            render={(s) => <><td>{s.NomeCompleto}</td><td>{s.Parentesco}</td><td>{s.DoencaCronica}</td><td>{s.Deficiencia}</td><td>{s.EmTratamento}</td></>} />
        </Secao>

        {d.Secoes.map((sec) => (
          <Secao key={sec.Secao} titulo={sec.Secao}>
            {sec.Itens.map((it, i) => (
              <Box key={i} sx={{ display: "flex", gap: 0.5, fontSize: 12, py: 0.2 }}>
                <Typography component="span" sx={{ fontWeight: 700, fontSize: 12, minWidth: 0 }}>{it.Pergunta}:</Typography>
                <Typography component="span" sx={{ fontSize: 12 }}>{it.Resposta || "—"}</Typography>
              </Box>
            ))}
          </Secao>
        ))}

        <Secao titulo="VII. Documentos anexados">
          <Tabela colunas={["Categoria", "Arquivo", "Status", "Motivo"]} linhas={d.Anexos}
            render={(a) => <><td>{a.Categoria}</td><td>{a.NomeOriginal}</td><td>{STATUS[a.Status] || a.Status}</td><td>{a.MotivoRecusa}</td></>} />
        </Secao>

        {(d.ParecerAnalista || d.DtAnalise) && (
          <Secao titulo="VIII. Parecer do analista">
            <Campo rotulo="Parecer" valor={d.ParecerAnalista} />
            <Campo rotulo="Analista" valor={d.UsuarioAnalise} />
            <Campo rotulo="Data" valor={data(d.DtAnalise)} />
          </Secao>
        )}
      </Box>
    </Box>
  );
}
